/* ==========================================================================
   Third Door — motion controller

   Loaded only when Theme settings -> Motion is on. Roughly 4KB, no library.

   Architecture notes, because the easy version of this file is a performance
   disaster:

   - One IntersectionObserver handles every reveal on the page, and unobserves
     each element after it fires. Reveals are one-way; nothing re-hides on
     scroll-up, which is both cheaper and less irritating.
   - Exactly one scroll listener exists, passive, and it only sets CSS custom
     properties inside a requestAnimationFrame. No layout is read during a
     scroll — all measurements are taken on load and on a debounced resize.
   - Pointer effects bind only on devices that match
     (hover: hover) and (pointer: fine), so a phone never runs them.
   - If the visitor prefers reduced motion, everything is revealed
     immediately and no listener is ever attached.
   ========================================================================== */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ------------------------------------------------------------------
     Reveal everything and stop. Used for reduced-motion visitors and as
     the failure path if IntersectionObserver is unavailable.
     ------------------------------------------------------------------ */

  function revealAll() {
    document.querySelectorAll('[data-reveal], [data-wipe], .split-word, .hero-illo').forEach(function (el) {
      el.classList.add('is-in');
    });

    // The hero stepper is scroll-driven rather than reveal-driven, so it needs
    // explicitly completing: full rail, every stage lit.
    var illo = document.querySelector('.hero-illo');
    if (illo) {
      illo.style.setProperty('--illo-progress', '1');
      illo.querySelectorAll('[data-illo-step]').forEach(function (s) {
        s.classList.add('is-active');
      });
    }
  }

  /* ------------------------------------------------------------------
     Stagger indices

     Children of a [data-reveal-group] get --i set to their position, which
     motion.css turns into a transition-delay. Doing this in JS rather than
     :nth-child means it works for any number of blocks the merchant adds.
     ------------------------------------------------------------------ */

  function assignStagger() {
    document.querySelectorAll('[data-reveal-group]').forEach(function (group) {
      var kids = group.querySelectorAll(':scope > [data-reveal], :scope > * > [data-reveal]');
      Array.prototype.forEach.call(kids, function (kid, i) {
        // A --i written in Liquid is deliberate authoring — the hero's trust
        // markers sequence after the headline, not from zero. Don't clobber it.
        if (kid.style.getPropertyValue('--i')) return;
        // Cap the delay so a collection page of 24 cards doesn't leave the last
        // one waiting two seconds to appear.
        kid.style.setProperty('--i', Math.min(i, 12));
      });
    });

    document.querySelectorAll('.drawer__list a').forEach(function (a, i) {
      a.style.setProperty('--i', i);
    });
  }

  /* ------------------------------------------------------------------
     Headline splitting

     Wraps each word in a span so it can be masked by its line box. The
     text content is unchanged, so screen readers and copy-paste are
     unaffected. Skipped on very long strings, where a per-word reveal
     reads as a novelty rather than a flourish.
     ------------------------------------------------------------------ */

  function splitHeadings() {
    document.querySelectorAll('[data-split]').forEach(function (el) {
      if (el.dataset.splitDone) return;

      var text = el.textContent.trim();
      if (!text || text.length > 130) return;

      var words = text.split(/\s+/);
      var frag = document.createDocumentFragment();

      words.forEach(function (word, i) {
        // The mask clips the word while it travels up into place. It is
        // inline-block, so words still flow and wrap as normal text.
        var mask = document.createElement('span');
        mask.className = 'split-mask';

        var inner = document.createElement('span');
        inner.className = 'split-word';
        inner.style.setProperty('--w', i);
        inner.textContent = word;

        mask.appendChild(inner);
        frag.appendChild(mask);

        // A real space between masks, so the headline wraps at word
        // boundaries and copy-paste produces the original sentence.
        if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
      });

      el.textContent = '';
      el.appendChild(frag);
      el.dataset.splitDone = 'true';
    });
  }

  /* ------------------------------------------------------------------
     The single reveal observer
     ------------------------------------------------------------------ */

  function initReveals() {
    if (!('IntersectionObserver' in window)) { revealAll(); return; }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, {
      // Fire slightly before the element reaches the viewport edge so the
      // movement has finished by the time it is comfortably in view.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.08
    });

    // .hero-illo is observed so the isometric building can draw itself the
    // moment it appears, independently of scroll position.
    document.querySelectorAll('[data-reveal], [data-wipe], [data-split], .hero-illo').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------
     Scroll-linked: progress rule, stuck header, timeline rail draw

     All three share one passive listener and one rAF. Element positions
     are cached, so a scroll frame reads no geometry from the DOM.
     ------------------------------------------------------------------ */

  function initScrollEffects() {
    var progress = document.querySelector('[data-scroll-progress]');
    var header = document.querySelector('.header');
    var hero = document.querySelector('.hero');
    var rails = Array.prototype.slice.call(document.querySelectorAll('.timeline'));

    var illo = document.querySelector('.hero-illo');
    var illoSteps = illo
      ? Array.prototype.map.call(illo.querySelectorAll('[data-illo-step]'), function (el) {
          return { el: el, at: parseFloat(el.getAttribute('data-illo-step')) || 0 };
        })
      : [];

    var railBoxes = [];
    var docHeight = 0;
    var heroHeight = 0;
    var ticking = false;

    function measure() {
      docHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      heroHeight = hero ? Math.max(1, hero.offsetHeight) : 1;
      railBoxes = rails.map(function (rail) {
        var box = rail.getBoundingClientRect();
        var top = box.top + window.pageYOffset;
        return { el: rail, top: top, height: box.height };
      });
    }

    function frame() {
      var y = window.pageYOffset || document.documentElement.scrollTop;

      if (progress) {
        progress.style.setProperty('--progress', Math.min(1, Math.max(0, y / docHeight)).toFixed(4));
      }

      if (header) {
        header.classList.toggle('is-stuck', y > 12);
      }

      // 0 while the hero fills the screen, 1 once it has scrolled away.
      var heroProgress = Math.min(1, Math.max(0, y / heroHeight));

      if (hero) {
        hero.style.setProperty('--hero-scroll', heroProgress.toFixed(4));
      }

      // The hero illustration's rail and stage highlighting.
      //
      // The rail is deliberately run over 70% of the hero's height rather than
      // all of it: driving it over the full range means the last stage only
      // lights up at the instant the illustration leaves the screen, which
      // reads as nothing having happened.
      if (illo) {
        var p = Math.min(1, heroProgress / 0.7);
        illo.style.setProperty('--illo-progress', p.toFixed(4));

        for (var s = 0; s < illoSteps.length; s++) {
          illoSteps[s].el.classList.toggle('is-active', p >= illoSteps[s].at);
        }
      }

      // The rail fills as its midpoint passes the middle of the viewport.
      var mid = y + window.innerHeight * 0.62;
      for (var i = 0; i < railBoxes.length; i++) {
        var r = railBoxes[i];
        var pct = (mid - r.top) / Math.max(1, r.height);
        r.el.style.setProperty('--draw', Math.min(1, Math.max(0, pct)).toFixed(4));
      }

      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(frame);
    }

    var resizeTimer;
    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () { measure(); frame(); }, 150);
    }

    measure();
    frame();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    // Images finishing late change document height, which would leave the
    // progress rule lying about how much is left.
    window.addEventListener('load', function () { measure(); frame(); });
  }

  /* ------------------------------------------------------------------
     Pointer-tracked 3D tilt

     Desktop only. Values are written as CSS custom properties and the
     transform itself lives in motion.css, so the browser owns the
     interpolation and this handler stays trivial.
     ------------------------------------------------------------------ */

  function initTilt() {
    if (!finePointer.matches) return;

    document.querySelectorAll('[data-tilt]').forEach(function (card) {
      var box = null;
      var raf = null;

      function apply(e) {
        if (!box) return;
        var px = (e.clientX - box.left) / box.width;
        var py = (e.clientY - box.top) / box.height;

        // Centre the range on zero: -0.5 .. 0.5
        var ry = (px - 0.5) * 2;
        var rx = (py - 0.5) * -2;

        if (raf) return;
        raf = window.requestAnimationFrame(function () {
          card.style.setProperty('--ry', (ry * getMax(card)).toFixed(2) + 'deg');
          card.style.setProperty('--rx', (rx * getMax(card)).toFixed(2) + 'deg');
          raf = null;
        });
      }

      function getMax(el) {
        var v = window.getComputedStyle(el).getPropertyValue('--tilt-max');
        var n = parseFloat(v);
        return isNaN(n) ? 6 : n;
      }

      card.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse') return;
        box = card.getBoundingClientRect();
        card.classList.add('is-tilting');
        apply(e);
      });

      card.addEventListener('pointermove', apply);

      card.addEventListener('pointerleave', function () {
        card.classList.remove('is-tilting');
        card.style.removeProperty('--rx');
        card.style.removeProperty('--ry');
        box = null;
      });
    });
  }

  /* ------------------------------------------------------------------
     Hero: photograph leans toward the cursor
     ------------------------------------------------------------------ */

  function initHeroTilt() {
    if (!finePointer.matches) return;

    var hero = document.querySelector('.hero');
    if (!hero) return;

    var raf = null;

    hero.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse' || raf) return;
      raf = window.requestAnimationFrame(function () {
        var box = hero.getBoundingClientRect();
        var px = (e.clientX - box.left) / box.width - 0.5;
        var py = (e.clientY - box.top) / box.height - 0.5;
        hero.style.setProperty('--hero-ry', (px * 5).toFixed(2) + 'deg');
        hero.style.setProperty('--hero-rx', (py * -3.5).toFixed(2) + 'deg');
        raf = null;
      });
    });

    hero.addEventListener('pointerleave', function () {
      hero.style.setProperty('--hero-ry', '0deg');
      hero.style.setProperty('--hero-rx', '0deg');
    });
  }

  /* ------------------------------------------------------------------
     Vertical parallax on tagged media

     Uses its own IntersectionObserver so off-screen images cost nothing —
     the scroll maths only runs for figures currently in view.
     ------------------------------------------------------------------ */

  function initParallax() {
    if (!finePointer.matches || !('IntersectionObserver' in window)) return;

    var nodes = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
    if (!nodes.length) return;

    var live = [];
    var ticking = false;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = live.indexOf(entry.target);
        if (entry.isIntersecting && i === -1) live.push(entry.target);
        else if (!entry.isIntersecting && i > -1) live.splice(i, 1);
      });
      if (live.length) onScroll();
    }, { rootMargin: '15% 0px 15% 0px' });

    nodes.forEach(function (n) { observer.observe(n); });

    function frame() {
      var vh = window.innerHeight;
      live.forEach(function (el) {
        var box = el.getBoundingClientRect();
        // -1 (below viewport) .. 1 (above viewport)
        var rel = (box.top + box.height / 2 - vh / 2) / vh;
        var depth = parseFloat(el.getAttribute('data-parallax')) || 24;
        el.style.setProperty('--p', (rel * depth * -1).toFixed(2));
      });
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(frame);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function start() {
    assignStagger();

    if (reduced.matches) {
      // No splitting, no observers, no listeners. Just show the content.
      revealAll();
      return;
    }

    var root = document.documentElement;

    splitHeadings();
    initReveals();
    initScrollEffects();

    if (root.getAttribute('data-tilt-enabled') !== 'false') {
      initTilt();
      initHeroTilt();
    }
    if (root.getAttribute('data-parallax-enabled') !== 'false') {
      initParallax();
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(start);

  // If the visitor changes the OS setting mid-session, honour it without a
  // reload rather than leaving them mid-animation.
  if (typeof reduced.addEventListener === 'function') {
    reduced.addEventListener('change', function (e) {
      if (e.matches) revealAll();
    });
  }

  // Shopify's theme editor injects sections after load; re-run so newly
  // added blocks are not left invisible.
  document.addEventListener('shopify:section:load', function () {
    assignStagger();
    if (reduced.matches) { revealAll(); return; }
    splitHeadings();
    initReveals();
    initTilt();
  });
})();
